import { api, type SharedFolderSummary } from '../api/client';
import { resolveFolderShareLabel } from '../crypto/shareLabelUpload';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { tInfoShareStatus } from '../i18n/status';
import { confirmDeleteSharedFolder } from '../ui/confirmDialog';
import { cacheSharedFolderLabel, cacheSharedFolders, getCachedSharedFolderLabel, getCachedSharedFolders } from '../ui/filesSharesNav';
import { isExternalFileDrag } from '../ui/filesDropUpload';
import { iconImg } from '../ui/icons';
import { showSharedFolderCreateDialog } from '../ui/sharedFolderCreateDialog';
import {
  canExtendSharedFolder,
  showSharedFolderExtendDialog,
} from '../ui/sharedFolderExtendDialog';
import { createHelpTip } from '../help/helpTips';
import { consumeCreateIntent } from '../app/navigationState';
import { enhanceDataTableCards } from '../ui/dataTableCards';
import { mountFilesFolderDetail, type FilesFolderDetailHandle } from './FilesFolderPage';

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString();
}

function tFolderPermission(permission: string): string {
  switch (permission) {
    case 'Full':
      return t('files.owner.accessFull');
    case 'ReadWrite':
      return t('files.owner.accessReadWrite');
    case 'Read':
      return t('files.owner.accessRead');
    default:
      return permission;
  }
}

function canDeleteFolder(folder: SharedFolderSummary): boolean {
  return (
    folder.permission === 'Full'
    && folder.displayStatus !== 'Revoked'
    && folder.displayStatus !== 'Purged'
  );
}

function getFolderIdFromPath(): string | null {
  const match = window.location.pathname.match(/^\/files\/([^/]+)$/);
  return match?.[1] ?? null;
}

function getFilesBrowseQuery(): string {
  return window.location.search;
}

function prefersFilesMotion(): boolean {
  return (
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    && !window.matchMedia('(max-width: 768px)').matches
  );
}

export function FilesPage(): HTMLElement {
  const page = document.createElement('section');
  page.className = 'module-page module-page--files';

  page.innerHTML = `
    <div class="files-workspace files-browse-layout" data-files-workspace>
      <div class="files-workspace__gutter" aria-hidden="true"></div>
      <div class="files-workspace__list files-list-pane">
        <section class="module-section files-list-section">
          <div class="module-section__header module-section__header--split">
            <div class="module-section__header-main">
              <h2 data-i18n="files.owner.listTitle"></h2>
            </div>
            <button type="button" class="button primary" id="files-show-create">
              <span class="button__icon"></span>
              <span data-i18n="files.owner.create"></span>
            </button>
          </div>
          <div id="files-list" class="files-table-wrap"></div>
          <p class="error-text" id="files-list-error" hidden></p>
        </section>
      </div>
      <div id="files-folder-detail" class="panel files-folder-panel files-browse-main files-workspace__detail"></div>
    </div>
  `;

  const workspace = page.querySelector('[data-files-workspace]') as HTMLElement;
  page.querySelector('#files-show-create .button__icon')?.append(iconImg('files', 18));
  applyI18n(page);
  page.querySelector('.files-list-section h2')?.append(createHelpTip('files'));

  const denyExternalFileDrop = (event: DragEvent) => {
    if (!isExternalFileDrag(event)) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'none';
    }
  };
  workspace.addEventListener('dragover', denyExternalFileDrop, true);
  workspace.addEventListener('drop', denyExternalFileDrop, true);

  const listEl = page.querySelector('#files-list') as HTMLElement;
  const listErrorEl = page.querySelector('#files-list-error') as HTMLParagraphElement;
  const showCreateButton = page.querySelector('#files-show-create') as HTMLButtonElement;
  const detailEl = page.querySelector('#files-folder-detail') as HTMLElement;
  const labelByFolderId = new Map<string, string>();
  const folderById = new Map<string, SharedFolderSummary>();
  let detailHandle: FilesFolderDetailHandle | null = null;
  let mountedFolderId: string | null = null;
  let lastBrowseQuery = getFilesBrowseQuery();
  let allowMotion = false;

  const canOpenFolder = (folder: SharedFolderSummary | undefined): boolean =>
    Boolean(folder)
    && folder!.displayStatus !== 'Expired'
    && folder!.displayStatus !== 'Revoked'
    && folder!.displayStatus !== 'Purged'
    && new Date(folder!.expiresAt).getTime() > Date.now();

  const disposeDetail = () => {
    detailHandle?.dispose();
    detailHandle = null;
  };

  const showFolderUnavailable = (reasonKey: string) => {
    disposeDetail();
    detailEl.innerHTML = `<p class="error-text" data-i18n="${reasonKey}"></p>`;
    applyI18n(detailEl);
  };

  const markActiveRows = (folderId: string | null) => {
    for (const row of listEl.querySelectorAll<HTMLTableRowElement>('tr[data-folder-id]')) {
      const active = row.dataset.folderId === folderId;
      row.classList.toggle('is-active', active);
      if (active) {
        row.setAttribute('aria-current', 'page');
      } else {
        row.removeAttribute('aria-current');
      }
    }
  };

  const syncRoute = () => {
    const folderId = getFolderIdFromPath();
    const animate = allowMotion && prefersFilesMotion();
    const closing = Boolean(mountedFolderId) && !folderId;
    workspace.classList.toggle('files-workspace--instant', !animate);
    workspace.classList.toggle('files-workspace--closing', animate && closing);
    workspace.classList.toggle('files-workspace--open', Boolean(folderId));
    markActiveRows(folderId);

    if (folderId === mountedFolderId) {
      const browseQuery = getFilesBrowseQuery();
      if (browseQuery !== lastBrowseQuery) {
        lastBrowseQuery = browseQuery;
        workspace.dispatchEvent(new Event('files:browse'));
      }
      return;
    }

    lastBrowseQuery = getFilesBrowseQuery();
    const previousFolderId = mountedFolderId;
    mountedFolderId = folderId;

    const afterCloseMotion = (done: () => void) => {
      if (!animate) {
        done();
        return;
      }
      let finished = false;
      const finish = () => {
        if (finished) {
          return;
        }
        finished = true;
        workspace.removeEventListener('transitionend', onTransitionEnd);
        done();
      };
      const onTransitionEnd = (event: TransitionEvent) => {
        if (event.target !== workspace || event.propertyName !== 'grid-template-columns') {
          return;
        }
        finish();
      };
      workspace.addEventListener('transitionend', onTransitionEnd);
      window.setTimeout(finish, 1100);
    };

    if (!folderId) {
      disposeDetail();
      afterCloseMotion(() => {
        if (getFolderIdFromPath()) {
          return;
        }
        detailEl.replaceChildren();
        workspace.classList.remove('files-workspace--closing');
      });
      return;
    }

    workspace.classList.remove('files-workspace--closing');

    const folder = folderById.get(folderId);
    if (folder && !canOpenFolder(folder)) {
      showFolderUnavailable(
        folder.displayStatus === 'Revoked' || folder.displayStatus === 'Purged'
          ? 'files.owner.unavailableFolder'
          : 'files.owner.expiredCannotOpen',
      );
      return;
    }

    const canReuseDetail = Boolean(
      detailHandle
      && previousFolderId
      && detailEl.querySelector('#files-folder-main'),
    );
    if (canReuseDetail && detailHandle) {
      detailHandle.switchFolder(folderId);
      return;
    }

    disposeDetail();
    detailHandle = mountFilesFolderDetail(detailEl, folderId);
  };

  const renderTableShell = () => {
    listEl.innerHTML = `
      <table class="data-table files-table">
        <thead>
          <tr>
            <th data-i18n="files.owner.listName"></th>
            <th class="files-table__extra" data-i18n="files.owner.displayStatus"></th>
            <th class="files-table__extra" data-i18n="files.owner.expiresAt"></th>
            <th class="files-table__extra" data-i18n="files.owner.listAccess"></th>
            <th class="data-table__actions-heading files-table__extra" data-i18n="files.owner.listActions"></th>
          </tr>
        </thead>
        <tbody id="files-table-body"></tbody>
      </table>
    `;
    applyI18n(listEl);
  };

  const renderRow = (folder: SharedFolderSummary, tbody: HTMLTableSectionElement) => {
    const row = document.createElement('tr');
    row.dataset.folderId = folder.id;

    const nameCell = document.createElement('td');
    const folderLabel =
      labelByFolderId.get(folder.id) ?? getCachedSharedFolderLabel(folder.id) ?? t('files.owner.untitledFolder');
    if (canOpenFolder(folder)) {
      const nameLink = document.createElement('a');
      nameLink.href = `/files/${folder.id}`;
      nameLink.className = 'files-table__name';
      nameLink.textContent = folderLabel;
      nameCell.append(nameLink);
    } else {
      const nameText = document.createElement('span');
      nameText.className = 'files-table__name files-table__name--unavailable';
      nameText.textContent = folderLabel;
      nameText.title = t(
        folder.displayStatus === 'Expired' ? 'files.owner.expiredCannotOpen' : 'files.owner.unavailableFolder',
      );
      nameCell.append(nameText);
    }

    const statusCell = document.createElement('td');
    statusCell.className = 'files-table__extra';
    const statusBadge = document.createElement('span');
    statusBadge.className = 'status-badge';
    statusBadge.textContent = tInfoShareStatus(folder.displayStatus);
    statusCell.append(statusBadge);

    const expiresCell = document.createElement('td');
    expiresCell.className = 'files-table__expires files-table__extra';
    expiresCell.textContent = formatDateTime(folder.expiresAt);

    const accessCell = document.createElement('td');
    accessCell.className = 'files-table__extra';
    accessCell.textContent = tFolderPermission(folder.permission);

    const actionsCell = document.createElement('td');
    actionsCell.className = 'data-table__actions files-table__extra';
    const actionsWrap = document.createElement('div');
    actionsWrap.className = 'data-table__actions-inner';
    actionsCell.append(actionsWrap);

    if (canExtendSharedFolder(folder)) {
      const extendButton = document.createElement('button');
      extendButton.type = 'button';
      extendButton.className = 'button ghost';
      extendButton.innerHTML = `<span data-i18n="files.owner.extend"></span>`;
      extendButton.addEventListener('click', () => {
        void (async () => {
          const updated = await showSharedFolderExtendDialog(folder);
          if (!updated) {
            return;
          }

          folder.expiresAt = updated.expiresAt;
          folder.canExtend = updated.canExtend;
          folder.maxExtendDays = updated.maxExtendDays;
          expiresCell.textContent = formatDateTime(updated.expiresAt);
          listErrorEl.hidden = true;
          if (!canExtendSharedFolder(folder)) {
            extendButton.remove();
          }
        })();
      });
      actionsWrap.append(extendButton);
    }

    if (canDeleteFolder(folder)) {
      const deleteButton = document.createElement('button');
      deleteButton.type = 'button';
      deleteButton.className = 'button ghost';
      deleteButton.dataset.deleteFolder = folder.id;
      deleteButton.innerHTML = `<span data-i18n="files.owner.delete"></span>`;
      deleteButton.addEventListener('click', () => {
        void (async () => {
          if (!(await confirmDeleteSharedFolder())) {
            return;
          }

          try {
            await api.deleteSharedFolder(folder.id);
            labelByFolderId.delete(folder.id);
            listErrorEl.hidden = true;
            row.remove();
            if (tbody.children.length === 0) {
              renderEmptyState();
            }
            if (mountedFolderId === folder.id) {
              history.pushState({}, '', '/files');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }
          } catch {
            listErrorEl.hidden = false;
            listErrorEl.textContent = t('status.error');
          }
        })();
      });
      actionsWrap.append(deleteButton);
    }

    if (actionsWrap.children.length > 0) {
      applyI18n(actionsWrap);
    }

    row.append(nameCell, statusCell, expiresCell, accessCell, actionsCell);
    tbody.append(row);
  };

  const renderEmptyState = () => {
    listEl.replaceChildren();
    const empty = document.createElement('p');
    empty.className = 'muted';
    empty.setAttribute('data-i18n', 'files.owner.listEmpty');
    listEl.append(empty);
    applyI18n(listEl);
  };

  const renderList = (folders: SharedFolderSummary[]) => {
    labelByFolderId.clear();
    folderById.clear();
    cacheSharedFolders(folders);
    for (const folder of folders) {
      folderById.set(folder.id, folder);
      const cachedLabel = getCachedSharedFolderLabel(folder.id);
      if (cachedLabel) {
        labelByFolderId.set(folder.id, cachedLabel);
      }
    }

    if (folders.length === 0) {
      renderEmptyState();
      markActiveRows(getFolderIdFromPath());
      return;
    }

    renderTableShell();
    const tbody = listEl.querySelector('#files-table-body') as HTMLTableSectionElement;

    for (const folder of folders) {
      renderRow(folder, tbody);

      if (folder.labelBlobId) {
        void resolveFolderShareLabel(folder.id, folder.wrappedFolderKey, folder.labelBlobId)
          .then((label) => {
            const trimmed = label?.trim();
            if (!trimmed) {
              return;
            }

            labelByFolderId.set(folder.id, trimmed);
            cacheSharedFolderLabel(folder.id, trimmed);
            const link = tbody.querySelector(
              `tr[data-folder-id="${folder.id}"] .files-table__name`,
            ) as HTMLAnchorElement | null;
            if (link) {
              link.textContent = trimmed;
            }
          })
          .catch(() => {
            // keep fallback title
          });
      }
    }

    const listTable = listEl.querySelector('table.data-table') as HTMLTableElement | null;
    if (listTable) {
      enhanceDataTableCards(listTable);
    }

    markActiveRows(getFolderIdFromPath());

    const openId = getFolderIdFromPath();
    if (openId) {
      const openFolder = folderById.get(openId);
      if (openFolder && !canOpenFolder(openFolder)) {
        mountedFolderId = openId;
        workspace.classList.add('files-workspace--open');
        showFolderUnavailable(
          openFolder.displayStatus === 'Revoked' || openFolder.displayStatus === 'Purged'
            ? 'files.owner.unavailableFolder'
            : 'files.owner.expiredCannotOpen',
        );
      }
    }
  };

  const loadList = async () => {
    const folders = await api.listSharedFolders();
    renderList(folders);
  };

  const openCreateDialog = () => {
    void (async () => {
      const created = await showSharedFolderCreateDialog();
      if (!created) {
        return;
      }

      if (created.label) {
        cacheSharedFolderLabel(created.id, created.label);
      }

      if (created.inviteFailures && created.inviteFailures > 0) {
        window.alert(t('files.owner.createInvitePartialFail', {
          failed: created.inviteFailures,
          total: created.inviteAttempts ?? created.inviteFailures,
        }));
      }

      try {
        await loadList();
      } catch {
        listEl.innerHTML = `<p class="error-text" data-i18n="status.error"></p>`;
        applyI18n(listEl);
      }

      history.pushState({}, '', `/files/${created.id}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    })();
  };

  showCreateButton.addEventListener('click', openCreateDialog);

  if (consumeCreateIntent()) {
    openCreateDialog();
  }

  workspace.addEventListener('files:route', () => {
    syncRoute();
  });

  const cachedFolders = getCachedSharedFolders();
  if (cachedFolders) {
    renderList(cachedFolders);
  }

  syncRoute();
  requestAnimationFrame(() => {
    allowMotion = true;
    if (prefersFilesMotion()) {
      workspace.classList.remove('files-workspace--instant');
    }
  });

  void loadList().catch(() => {
    listEl.innerHTML = `<p class="error-text" data-i18n="status.error"></p>`;
    applyI18n(listEl);
  });

  bindLocaleRefresh(() => {
    void loadList();
  });

  const observer = new MutationObserver(() => {
    if (!page.isConnected) {
      disposeDetail();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return page;
}
